import React, { FC, useState, useEffect } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Flex, IconButton, Popover } from '@ui'
import { DateUtil } from '@utils/dateUtil'
import { IUploadedImageAsset } from '@api'
import ImageItemError from './ImageItemError'
import { Menu, MenuItem, Position } from '@blueprintjs/core'
import css from './imageItem.module.scss'
import truncateFilename, { getFileExtension } from '../../utils/truncateFilename'

type ImageItemPropsT = {
  asset: IUploadedImageAsset
  onDeleteImage: () => void
  onClickDefaultImage: () => void
  isDefault: boolean
}

const ImageItem: FC<React.PropsWithChildren<ImageItemPropsT>> = ({
  onDeleteImage,
  asset,
  onClickDefaultImage,
  isDefault,
}) => {
  const [isError, setIsError] = useState(false)
  const { id, fileName, updatedAt, url } = asset
  const isPdf = getFileExtension(fileName) === 'pdf'

  const formattedUploadedDate = DateUtil(updatedAt).fullDateFormat()
  const setWithError = () => setIsError(true)
  const setWithoutError = () => setIsError(false)

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`
  })

  if (isError) {
    return <ImageItemError fileName={fileName} onRemove={onDeleteImage} />
  }

  return (
    <Flex data-test={`upload-image-item-${id}`} className={css.wrapper} align="center">
      <div className={css.preview}>
        {isPdf ? (
          <Document
            file={url}
            renderMode="svg"
            onLoadError={setWithError}
            onSourceError={setWithError}
            onLoadSuccess={setWithoutError}
          >
            <Page pageNumber={1} />
          </Document>
        ) : (
          <img src={url} alt="preview image" onError={setWithError} onLoad={setWithoutError} />
        )}
      </div>
      <div className={css.file}>
        <Flex className={css.link_wrapper} align="baseline">
          <a className={css.link} href={url} download={fileName} title={fileName}>
            {truncateFilename(fileName, updatedAt)}
          </a>
          {isDefault && <span className={css.default}>Default</span>}
          <div className={css.menu}>
            <Popover
              minimal
              usePortal
              captureDismiss
              position={Position.BOTTOM}
              modifiers={{
                arrow: { enabled: false },
                flip: { enabled: false },
              }}
              content={
                <Menu>
                  <MenuItem
                    text="Set as Default"
                    disabled={isDefault}
                    onClick={onClickDefaultImage}
                  />
                  <MenuItem text="Delete Image" onClick={onDeleteImage} />
                </Menu>
              }
            >
              <IconButton
                data-test="upload-image-action-button"
                className={css.menu_button}
                round={false}
                tooltip="More actions"
                iconName="ellipsis"
              />
            </Popover>
          </div>
        </Flex>
        <div data-test="upload-image-item-updatedAt" className={css.uploaded_date}>
          Uploaded on {formattedUploadedDate}
        </div>
      </div>
    </Flex>
  )
}

export default ImageItem
